import axios from 'axios';
import { Account, LoginCredentials, PasswordChange, PasswordResetFinish } from '@appTypes/auth.types';
import { API_ENDPOINTS } from '@config/constants';
import { StorageService } from '@utils/storage';

// Rate limiting for password reset
const RESET_ATTEMPT_KEY = 'password_reset_attempts';
const MAX_RESET_ATTEMPTS = 3;
const RESET_TIMEOUT = 30 * 60 * 1000; // 30 minutes

/**
 * Authentication service for session-based auth with Spring Security
 * Uses form-urlencoded POST to /api/authentication (NOT JWT)
 */
export class AuthService {
    /**
     * Login with session-based authentication
     * Matches Angular's auth-session.service.ts login method
     */
    static async login(credentials: LoginCredentials): Promise<void> {
        // Build form-urlencoded body matching Spring Security format
        const formData = new URLSearchParams();
        formData.append('j_username', credentials.username);
        formData.append('j_password', credentials.password);
        formData.append('remember-me', String(credentials.rememberMe));
        formData.append('submit', 'Login');

        try {
            await axios.post(API_ENDPOINTS.AUTHENTICATION, formData.toString(), {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                withCredentials: true, // Important: include cookies
            });

            // Store rememberMe preference for subscriber storage decision
            StorageService.setRememberMe(credentials.rememberMe);
        } catch (error) {
            // Provide user-friendly error messages
            if (axios.isAxiosError(error)) {
                console.error('[AuthService] Login error:', error.response?.status, error.response?.data, error.message);
                if (error.response?.status === 401) {
                    throw new Error('Invalid username or password. Please try again.');
                }
                if (error.response?.status === 403) {
                    throw new Error('Your account has been locked or disabled. Please contact support.');
                }
                if (!error.response) {
                    throw new Error('Unable to connect to the server. Please check your internet connection.');
                }
                throw new Error(`Unexpected server error (${error.response.status}). Please try again later.`);
            }
            console.error('[AuthService] Non-Axios login error:', error);
            throw new Error('An unexpected error occurred. Please try again later.');
        }
    }

    /**
     * Logout from server and get fresh CSRF token
     * Matches Angular's auth-session.service.ts logout method
     */
    static async logout(): Promise<void> {
        try {
            await axios.post(
                API_ENDPOINTS.LOGOUT,
                {},
                {
                    withCredentials: true,
                }
            );
        } catch {
            // Ignore logout errors
        }

        // Get a fresh CSRF token by calling account endpoint
        try {
            await axios.get(API_ENDPOINTS.ACCOUNT, { withCredentials: true });
        } catch {
            // Expected to fail if not authenticated
        }

        // Clear local storage
        StorageService.clearAll();
    }

    /**
     * Fetch current user account info
     */
    static async getAccount(): Promise<Account> {
        const response = await axios.get<Account>(API_ENDPOINTS.ACCOUNT, {
            withCredentials: true,
        });
        return response.data;
    }

    /**
     * Impersonate another user (admin only)
     * Matches Angular's auth-session.service.ts impersonate method
     */
    static async impersonate(username: string): Promise<void> {
        const formData = new URLSearchParams();
        formData.append('username', username);

        await axios.post(API_ENDPOINTS.IMPERSONATE, formData.toString(), {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            withCredentials: true,
        });

        StorageService.setImpersonation(username);
    }

    /**
     * End impersonation
     */
    static async endImpersonation(): Promise<void> {
        StorageService.setImpersonation(null);
        // Reload to get back to original user session
        window.location.reload();
    }

    /**
     * Activate account with key from email
     */
    static async activateAccount(key: string): Promise<void> {
        await axios.get(`${API_ENDPOINTS.ACTIVATE}?key=${encodeURIComponent(key)}`, {
            withCredentials: true,
        });
    }

    /**
     * Request password reset email
     */
    static async requestPasswordReset(email: string): Promise<void> {
        // Check rate limiting
        const attempts = this.getRateLimitInfo();
        if (attempts.count >= MAX_RESET_ATTEMPTS && Date.now() - attempts.timestamp < RESET_TIMEOUT) {
            const minutesLeft = Math.ceil((RESET_TIMEOUT - (Date.now() - attempts.timestamp)) / 60000);
            throw new Error(`Too many reset attempts. Please try again in ${minutesLeft} minutes.`);
        }

        try {
            await axios.post(API_ENDPOINTS.PASSWORD_RESET_INIT, email, {
                headers: {
                    'Content-Type': 'text/plain',
                },
                withCredentials: true,
            });
        } catch (err) {
            // The backend returns 400 (EmailNotFoundException) when the address
            // isn't registered. Surface a clear message instead of the raw
            // "Request failed with status code 400".
            if (axios.isAxiosError(err) && err.response?.status === 400) {
                throw new Error(
                    'No account is registered with that email address. Please check it and try again.'
                );
            }
            throw err;
        }

        // Update rate limiting
        this.updateRateLimit();
    }

    /**
     * Complete password reset with key from email
     */
    static async finishPasswordReset(data: PasswordResetFinish): Promise<void> {
        try {
            await axios.post(API_ENDPOINTS.PASSWORD_RESET_FINISH, data, {
                withCredentials: true,
            });
        } catch (err) {
            // The backend returns 400 when the reset key is unknown, already used,
            // or expired (InvalidResetKeyException). Surface a clear, recoverable
            // message instead of the raw "Request failed with status code 500/400".
            // The word "expired" routes ResetPassword.tsx to its "Link Expired" view.
            if (axios.isAxiosError(err) && err.response?.status === 400) {
                const title = String((err.response.data as { title?: string })?.title ?? '');
                if (/password/i.test(title)) {
                    // InvalidPasswordException (password fails the server policy).
                    throw new Error('Please choose a password that meets all requirements.');
                }
                throw new Error(
                    'This password reset link is invalid or has expired. Please request a new one.'
                );
            }
            throw err;
        }
        // Clear rate limiting after successful reset
        this.clearRateLimit();
    }

    /**
     * Reset password with key and new password
     * Convenience wrapper for finishPasswordReset
     */
    static async resetPassword(key: string, newPassword: string): Promise<void> {
        return this.finishPasswordReset({ key, newPassword });
    }

    /**
     * Change password for current user
     */
    static async changePassword(data: PasswordChange): Promise<void> {
        await axios.post(API_ENDPOINTS.PASSWORD_CHANGE, data, {
            withCredentials: true,
        });
    }

    /**
     * Get current impersonation username
     */
    static getImpersonation(): string | null {
        return StorageService.getImpersonation();
    }

    // Rate limiting helpers
    private static getRateLimitInfo(): { count: number; timestamp: number } {
        const stored = localStorage.getItem(RESET_ATTEMPT_KEY);
        if (!stored) {
            return { count: 0, timestamp: Date.now() };
        }
        try {
            return JSON.parse(stored);
        } catch {
            return { count: 0, timestamp: Date.now() };
        }
    }

    private static updateRateLimit(): void {
        const attempts = this.getRateLimitInfo();
        const now = Date.now();

        // Reset count if timeout has passed
        if (now - attempts.timestamp >= RESET_TIMEOUT) {
            localStorage.setItem(
                RESET_ATTEMPT_KEY,
                JSON.stringify({
                    count: 1,
                    timestamp: now,
                })
            );
        } else {
            localStorage.setItem(
                RESET_ATTEMPT_KEY,
                JSON.stringify({
                    count: attempts.count + 1,
                    timestamp: attempts.timestamp,
                })
            );
        }
    }

    private static clearRateLimit(): void {
        localStorage.removeItem(RESET_ATTEMPT_KEY);
    }
}

export default AuthService;
